import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Users, Download, CheckCircle, XCircle, UserCheck, RefreshCcw, Search, Globe, Mail, Phone } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface XpmClient {
    ID: string;
    Name: string;
    Email: string;
    Phone: string;
    Fax: string;
    Address: string;
    City: string;
    Region: string;
    PostCode: string;
    Country: string;
    PostalAddress: string;
    PostalCity: string;
    PostalRegion: string;
    PostalPostCode: string;
    PostalCountry: string;
    AccountManager: string;
    JobManager: string;
    BillingClient: string;
    TaxNumber: string;
    CompanyNumber: string;
    BusinessNumber: string;
    Website: string;
    ReferralSource: string;
    ExportCode: string;
    IsProspect: boolean;
    IsArchived: boolean;
    IsDeleted: boolean;
    DateOfBirth: string;
    Type: string;
    Status: string;
}

const XeroClients: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [clients, setClients] = useState<XpmClient[]>([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [managerFilter, setManagerFilter] = useState<string>("all");
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (accounts.length === 0) return;
        setLoading(true);
        setError(null);
        try {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setLoading(false); return; }
            const response = await fetch(`${getApiBaseUrl()}/api/xero/practice-manager/clients`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.details || errData.error || `HTTP ${response.status}`);
            }
            const data: XpmClient[] = await response.json();
            setClients(data);
        } catch (err: any) {
            console.error("Failed to fetch Xero clients:", err);
            setError(err.message || "Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [accounts, instance, setLoading]);

    // Filter options
    const statuses = [...new Set(clients.map(c => c.Status).filter(Boolean))].sort();
    const types = [...new Set(clients.map(c => c.Type).filter(Boolean))].sort();
    const managers = [...new Set(clients.map(c => c.AccountManager).filter(Boolean))].sort();

    // Filtered data
    const filtered = clients.filter(c => {
        if (statusFilter !== "all" && c.Status !== statusFilter) return false;
        if (typeFilter !== "all" && c.Type !== typeFilter) return false;
        if (managerFilter !== "all" && c.AccountManager !== managerFilter) return false;
        if (search) {
            const q = search.toLowerCase();
            return (
                c.Name.toLowerCase().includes(q) ||
                c.Email.toLowerCase().includes(q) ||
                c.Phone.toLowerCase().includes(q) ||
                c.AccountManager.toLowerCase().includes(q) ||
                c.City.toLowerCase().includes(q) ||
                c.TaxNumber.toLowerCase().includes(q) ||
                c.CompanyNumber.toLowerCase().includes(q) ||
                c.Type.toLowerCase().includes(q) ||
                c.ExportCode.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // Stats
    const activeCount = clients.filter(c => c.Status === "Active").length;
    const prospectCount = clients.filter(c => c.Status === "Prospect").length;
    const archivedCount = clients.filter(c => c.Status === "Archived").length;
    const uniqueManagers = new Set(clients.map(c => c.AccountManager).filter(Boolean)).size;

    const columns: TableColumn<XpmClient>[] = [
        {
            name: "Name",
            selector: row => row.Name || "",
            sortable: true,
            cell: row => (
                <div style={{ fontWeight: 500 }}>
                    {row.Name || "—"}
                </div>
            ),
            grow: 2,
        },
        {
            name: "Status",
            selector: row => row.Status || "",
            sortable: true,
            cell: row => (
                <span className={`status-badge ${row.Status === "Active" ? "status-active" : row.Status === "Prospect" ? "status-pending" : "status-inactive"}`}>
                    {row.Status || "—"}
                </span>
            ),
            width: "110px",
            center: true,
        },
        {
            name: "Type",
            selector: row => row.Type || "",
            sortable: true,
            width: "150px",
            cell: row => <span>{row.Type || "—"}</span>,
        },
        {
            name: "Account Manager",
            selector: row => row.AccountManager || "",
            sortable: true,
            width: "170px",
            cell: row => <span>{row.AccountManager || "—"}</span>,
        },
        {
            name: "Email",
            selector: row => row.Email || "",
            sortable: true,
            cell: row =>
                row.Email ? (
                    <a href={`mailto:${row.Email}`} style={{ color: "var(--accent-blue)" }}>
                        {row.Email}
                    </a>
                ) : (
                    <span style={{ color: "var(--text-muted)" }}>—</span>
                ),
            width: "220px",
        },
        {
            name: "Phone",
            selector: row => row.Phone || "",
            sortable: true,
            width: "140px",
            cell: row => <span>{row.Phone || "—"}</span>,
        },
        {
            name: "City",
            selector: row => row.City || "",
            sortable: true,
            width: "130px",
            cell: row => <span>{row.City || "—"}</span>,
        },
        {
            name: "Tax Number",
            selector: row => row.TaxNumber || "",
            sortable: true,
            width: "130px",
            cell: row => <span>{row.TaxNumber || "—"}</span>,
        },
        {
            name: "Company No.",
            selector: row => row.CompanyNumber || "",
            sortable: true,
            width: "130px",
            cell: row => <span>{row.CompanyNumber || "—"}</span>,
        },
        {
            name: "Export Code",
            selector: row => row.ExportCode || "",
            sortable: true,
            width: "120px",
            cell: row => <span>{row.ExportCode || "—"}</span>,
        },
    ];

    const exportCSV = () => {
        if (filtered.length === 0) return;
        const headers = [
            "Name", "Status", "Type", "Account Manager", "Job Manager",
            "Email", "Phone", "Fax", "Address", "City", "Region", "Post Code", "Country",
            "Tax Number", "Company Number", "Business Number", "Website",
            "Referral Source", "Export Code"
        ];
        const rows = filtered.map(c => [
            c.Name, c.Status, c.Type, c.AccountManager, c.JobManager,
            c.Email, c.Phone, c.Fax, c.Address, c.City, c.Region, c.PostCode, c.Country,
            c.TaxNumber, c.CompanyNumber, c.BusinessNumber, c.Website,
            c.ReferralSource, c.ExportCode
        ]);
        const csvContent = [
            headers.join(","),
            ...rows.map(row =>
                row.map(val => {
                    const str = String(val || "").replace(/"/g, '""');
                    return str.includes(",") || str.includes('"') || str.includes("\n")
                        ? `"${str}"`
                        : str;
                }).join(",")
            ),
        ].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `xero-clients-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
    };

    const tableStyles = {
        table: {
            style: {
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '8px',
            },
        },
        headRow: {
            style: {
                backgroundColor: 'var(--bg-tertiary)',
                borderBottomColor: 'var(--border-color)',
                color: 'var(--text-primary)',
                fontWeight: 600,
                fontSize: '0.85rem',
            },
        },
        headCells: {
            style: {
                color: 'var(--text-secondary)',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                borderBottomColor: 'var(--border-color)',
            },
            highlightOnHoverStyle: {
                backgroundColor: 'var(--bg-hover)',
                color: 'var(--text-primary)',
                borderBottomColor: 'var(--border-color)',
            },
        },
        cells: {
            style: {
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        pagination: {
            style: {
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-secondary)',
                borderTopColor: 'var(--border-color)',
            },
            pageButtonsStyle: {
                color: 'var(--text-secondary)',
                fill: 'var(--text-secondary)',
                '&:hover:not(:disabled)': {
                    backgroundColor: 'var(--bg-hover)',
                },
                '&:disabled': {
                    opacity: 0.4,
                },
            },
        },
        noData: {
            style: {
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-secondary)',
            },
        },
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <Users size={20} />
                    <h1>Xero Clients</h1>
                </div>
            </div>

            {/* Stats */}
            <div className="stats-grid stats-grid-5">
                <div className="stat-card">
                    <div className="stat-icon"><Users size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{clients.length}</div>
                        <div className="stat-label">Total Clients</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><CheckCircle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{activeCount}</div>
                        <div className="stat-label">Active</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><Search size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{prospectCount}</div>
                        <div className="stat-label">Prospects</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><XCircle size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{archivedCount}</div>
                        <div className="stat-label">Archived</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon"><UserCheck size={20} /></div>
                    <div className="stat-content">
                        <div className="stat-value">{uniqueManagers}</div>
                        <div className="stat-label">Account Managers</div>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{
                    background: "var(--error-bg, #fef2f2)",
                    color: "var(--error-color, #dc2626)",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 16,
                    border: "1px solid var(--error-border, #fecaca)"
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Controls */}
            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search clients..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="search-input"
                />
                <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="filter-select"
                >
                    <option value="all">All Statuses</option>
                    {statuses.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
                {types.length > 0 && (
                    <select
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Types</option>
                        {types.map(t => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                )}
                {managers.length > 0 && (
                    <select
                        value={managerFilter}
                        onChange={e => setManagerFilter(e.target.value)}
                        className="filter-select"
                    >
                        <option value="all">All Managers</option>
                        {managers.map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                )}
                <button className="action-btn" onClick={fetchData} title="Refresh">
                    <RefreshCcw size={16} />
                </button>
                <button className="download-btn" onClick={exportCSV} disabled={filtered.length === 0} title="Download as CSV">
                    <Download size={16} />
                    Export CSV
                </button>
            </div>

            {/* Data Table */}
            <div className="data-table-container">
                <DataTable
                    columns={columns}
                    data={filtered}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    defaultSortFieldId={1}
                    noDataComponent={
                        <div className="no-data">
                            {error ? "Failed to load data" : "No clients found"}
                        </div>
                    }
                    customStyles={tableStyles}
                />
            </div>
        </div>
    );
};

export default XeroClients;
